/**
 * Hand evaluation using pokersolver library
 */

import { Hand } from 'pokersolver'
import type { Card, CardValue, Winner, EnginePlayer, HandRank, Pot } from './types'

/**
 *
 * @param cards array of cards
 * @returns an array of the cards values
 * @example
 * getCardsValues(['8d', '2h']) // ['8', '2']
 * getCardsValues(['Jd', '5h']) // ['J', '5']
 * getCardsValues(['Ad', '3h']) // ['A', '3']
 */
export const getCardsValues = (cards: Card[]) => {
  return cards.map((card) => card[0] as CardValue)
}

/**
 * Evaluate a single player's hand
 * @param holeCards - Player's 2 hole cards
 * @param communityCards - Board cards
 * @returns Hand rank (typed) and description
 */
export function evaluateHand(
  holeCards: Card[],
  communityCards: Card[]
): {
  rank: HandRank
  description: string
} {
  const allCards = [...holeCards, ...communityCards]
  const hand = Hand.solve(allCards)

  return {
    rank: hand.name as HandRank, // ✅ Type assertion - pokersolver returns string
    description: hand.descr
  }
}

/**
 * Compare two hands to determine winner
 * @param hand1 - First hand cards
 * @param hand2 - Second hand cards
 * @param communityCards - Board cards
 * @returns -1 if hand1 wins, 1 if hand2 wins, 0 if tie
 */
export function compareHands(hand1: Card[], hand2: Card[], communityCards: Card[]): number {
  const cards1 = [...hand1, ...communityCards]
  const cards2 = [...hand2, ...communityCards]

  const solved1 = Hand.solve(cards1)
  const solved2 = Hand.solve(cards2)

  const winners = Hand.winners([solved1, solved2])

  if (winners.length === 2) {
    return 0 // Tie
  }

  if (winners[0] === solved1) {
    return -1 // hand1 wins
  }

  return 1 // hand2 wins
}

/**
 * Find winners from a list of active players
 * @internal Used internally by PokerEngine.completeHand()
 * External consumers should use PokerEngine.completeHand() which returns HandOutcome
 * @param players - Players still in the hand (not folded)
 * @param communityCards - Board cards
 * @returns Array of winners (can be multiple for ties)
 */
export function findWinners(players: EnginePlayer[], communityCards: Card[]): Winner[] {
  // Filter out folded players — they cannot win pots.
  // Critical fix: prevents folded players from "winning" when others go all-in.
  //
  // Also filter out players with NO hole cards. This protects against a class
  // of bugs where a player was added to the pot's eligibility list (e.g. via
  // `BlindPolicy: 'force'` draining a sittingout player to `state: 'allin'`)
  // but never actually had cards dealt this hand. `Hand.solve([])` would
  // evaluate the community cards alone (effectively a tie with every real
  // hand), splitting the pot into someone who isn't even in the hand. Players
  // without cards cannot win a showdown by definition.
  const activePlayers = players.filter((p) => p.state !== 'folded' && p.cards.length > 0)

  if (activePlayers.length === 0) {
    return []
  }

  // If only one non-folded player, they win by default
  if (activePlayers.length === 1) {
    const player = activePlayers[0]
    const evaluation = evaluateHand(player.cards, communityCards)
    return [
      {
        playerId: player.id,
        userId: player.userId,
        playerName: player.name,
        amount: 0, // Pot amount will be set by caller
        hand: {
          rank: evaluation.rank,
          description: evaluation.description,
          cards: player.cards
        }
      }
    ]
  }

  // Evaluate all non-folded hands
  const evaluatedPlayers = activePlayers.map((player) => {
    const allCards = [...player.cards, ...communityCards]
    const hand = Hand.solve(allCards)
    return {
      player,
      hand,
      evaluation: {
        rank: hand.name as HandRank, // ✅ Type assertion
        description: hand.descr
      }
    }
  })

  // Find winners using pokersolver
  const hands = evaluatedPlayers.map((ep) => ep.hand)
  const winningHands = Hand.winners(hands)

  // Map winning hands back to players
  const winners: Winner[] = []

  for (const winningHand of winningHands) {
    const evaluated = evaluatedPlayers.find((ep) => ep.hand === winningHand)
    if (evaluated) {
      winners.push({
        playerId: evaluated.player.id,
        userId: evaluated.player.userId,
        playerName: evaluated.player.name,
        amount: 0, // Pot amount will be set by caller
        hand: {
          rank: evaluated.evaluation.rank,
          description: evaluated.evaluation.description,
          cards: evaluated.player.cards
        }
      })
    }
  }

  return winners
}

/**
 * Find winners for a specific pot
 * Similar to findWinners, but filters players by pot eligibility
 *
 * @param pot - The pot to evaluate (contains eligiblePlayerIds)
 * @param allPlayers - All players in the game
 * @param communityCards - Board cards
 * @returns Array of winners from eligible players (can be multiple for ties)
 */
export function findWinnersForPot(pot: Pot, allPlayers: EnginePlayer[], communityCards: Card[]): Winner[] {
  // Filter to only eligible players for this pot
  const eligiblePlayers = allPlayers.filter((player) => pot.eligiblePlayerIds.includes(player.id))

  // Use existing findWinners logic on filtered players
  return findWinners(eligiblePlayers, communityCards)
}
